/* LogoReviewCard — PROP-002 / Iter 87 (Correct & Approve).

   Per-outcome card in the Logo Review Queue. Operator-locked layout:

     • Open Domain is the PRIMARY navigation action (full-width, cyan).
     • Secondary action row (Iter 87 locked):
         Approve  ·  Correct & Approve  ·  Reject
       Nothing else.
     • Correct & Approve form (Iter 87) shows AI Proposed values
       as read-only context next to editable Correct inputs + a
       file upload + an optional audit note.

   Core principle (PROP-002):
     AI proposes. Human verifies. Repository updates only after approval.
*/

import { useRef, useState } from "react";
import {
    ArrowSquareOut,
    CheckCircle,
    XCircle,
    PencilSimple,
    Globe,
    Warning,
    UploadSimple,
    Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
    ConfidenceChip,
    LogoCandidatePreview,
    SourceMetaLine,
    ProvenanceBadges,
    ReviewStateChip,
} from "./enrichmentOutcomeChips";

export default function LogoReviewCard({ card, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("idle"); // idle | reject | correct
    const [rejectReason, setRejectReason] = useState("");
    const [correctedDomain, setCorrectedDomain] = useState(card.discovered_domain || "");
    const [correctedLogoUrl, setCorrectedLogoUrl] = useState(card.candidate_logo_url || "");
    const [correctedLogoFile, setCorrectedLogoFile] = useState(null);
    const [correctionNote, setCorrectionNote] = useState("");
    const fileInputRef = useRef(null);

    const isPending = card.review_state === "pending_review";
    const wasSkipped = !!card.skipped_at && isPending;
    const isApproved = card.review_state === "approved";
    const isHumanCorrected = card.review_state === "human_corrected";
    const isRejected = card.review_state === "rejected";

    async function _postJson(path) {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/data/admin/logos/review-queue/${card.outcome_id}${path}`,
                {},
            );
            return data;
        } finally {
            setBusy(false);
        }
    }

    async function _postReject(path, body) {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/data/admin/logos/review-queue/${card.outcome_id}${path}`,
                body,
            );
            return data;
        } finally {
            setBusy(false);
        }
    }

    async function handleApprove() {
        try {
            const r = await _postJson("/approve");
            if (r.repository_warning) {
                toast.warning(`Approved (audit) — but repository write missed: ${r.repository_warning}`);
            } else {
                toast.success(`Approved — ${r.final_domain || r.company_id} saved to repository.`);
            }
            onUpdated?.();
        } catch (e) {
            toast.error(`Approval failed: ${e.response?.data?.detail || e.message}`);
        }
    }

    async function handleReject() {
        if (rejectReason.trim().length < 5) {
            toast.error("Rejection reason must be at least 5 characters.");
            return;
        }
        try {
            await _postReject("/reject", { reason: rejectReason.trim() });
            toast.success("Rejected. The outcome is removed from the queue (7-day undo window).");
            setMode("idle");
            setRejectReason("");
            onUpdated?.();
        } catch (e) {
            toast.error(`Reject failed: ${e.response?.data?.detail || e.message}`);
        }
    }

    async function handleCorrectApprove() {
        // Client-side guard: at least one value must DIFFER from the AI proposal.
        const aiDomain = (card.discovered_domain || "").trim().toLowerCase();
        const aiLogo = card.candidate_logo_url || "";
        const newDomain = correctedDomain.trim();
        const newLogoUrl = correctedLogoUrl.trim();
        const domainChanged = !!newDomain && newDomain.toLowerCase() !== aiDomain;
        const logoUrlChanged = !!newLogoUrl && newLogoUrl !== aiLogo;
        const fileProvided = !!correctedLogoFile;
        if (!domainChanged && !logoUrlChanged && !fileProvided) {
            toast.error("Correct & Approve requires at least one change — otherwise use plain Approve.");
            return;
        }
        // File-size pre-flight: 2 MB cap matches backend guard.
        if (fileProvided && correctedLogoFile.size > 2 * 1024 * 1024) {
            toast.error("Logo file is larger than 2 MB. Please use a smaller file.");
            return;
        }

        setBusy(true);
        try {
            const form = new FormData();
            if (newDomain) form.append("corrected_domain", newDomain);
            if (newLogoUrl && !fileProvided) form.append("corrected_logo_url", newLogoUrl);
            if (fileProvided) form.append("corrected_logo_file", correctedLogoFile);
            if (correctionNote.trim()) form.append("note", correctionNote.trim());
            const { data: r } = await api.post(
                `/data/admin/logos/review-queue/${card.outcome_id}/correct-approve`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            if (r.repository_warning) {
                toast.warning(`Corrected (audit) — but repository write missed: ${r.repository_warning}`);
            } else {
                toast.success(`Corrected & Approved — ${r.final_domain || r.company_id} saved to repository.`);
            }
            setMode("idle");
            setCorrectedLogoFile(null);
            setCorrectionNote("");
            if (fileInputRef.current) fileInputRef.current.value = "";
            onUpdated?.();
        } catch (e) {
            toast.error(`Correct & Approve failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    }

    async function handleUndoReject() {
        try {
            await _postJson("/undo-reject");
            toast.success("Undo successful — outcome returned to Pending Review.");
            onUpdated?.();
        } catch (e) {
            toast.error(`Undo failed: ${e.response?.data?.detail || e.message}`);
        }
    }

    return (
        <article
            data-testid={`logo-review-card-${card.outcome_id}`}
            className={`border rounded-md p-4 bg-white transition-shadow ${
                wasSkipped ? "border-slate-200 opacity-80" : "border-slate-200 hover:shadow-sm"
            }`}
        >
            <div className="grid grid-cols-[128px_1fr] gap-4">
                {/* Logo preview — 128 px frame, left side */}
                <div className="h-32 w-32 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {card.candidate_logo_url ? (
                        <img
                            src={card.candidate_logo_url}
                            alt={`${card.company_name} logo candidate`}
                            referrerPolicy="no-referrer"
                            className="max-h-full max-w-full object-contain p-2"
                            data-testid={`logo-review-card-${card.outcome_id}-preview`}
                            onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                        />
                    ) : (
                        <LogoCandidatePreview url={null} />
                    )}
                </div>

                {/* Right column — meta + actions */}
                <div className="min-w-0 space-y-2.5">
                    {/* Title row */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`logo-review-card-${card.outcome_id}-name`}>
                                {card.company_name || card.company_id}
                            </div>
                            {card.discovered_domain ? (
                                <a
                                    href={`https://${card.discovered_domain}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-sm font-mono text-cyan-700 hover:text-cyan-800 hover:underline"
                                    data-testid={`logo-review-card-${card.outcome_id}-domain-link`}
                                >
                                    {card.discovered_domain}
                                    <ArrowSquareOut size={12} />
                                </a>
                            ) : (
                                <span className="text-xs text-slate-400">No domain discovered</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <ReviewStateChip state={card.review_state} />
                            {typeof card.confidence_score === "number" && (
                                <span
                                    className="font-mono text-sm tabular-nums text-slate-900 px-2 py-0.5 rounded bg-slate-100"
                                    title="Composite confidence score (V2.0)"
                                    data-testid={`logo-review-card-${card.outcome_id}-confidence`}
                                >
                                    {card.confidence_score.toFixed(3)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Source meta-line + confidence chip */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <SourceMetaLine outcome={card} />
                        {card.confidence && <ConfidenceChip value={card.confidence} />}
                    </div>

                    {/* Provenance badges */}
                    {card.badges?.length > 0 && <ProvenanceBadges badges={card.badges} />}

                    {/* PRIMARY ACTION: Open Domain.
                        Rendered as a styled <a> so middle-click / right-click /
                        screen readers all get native link semantics. */}
                    {card.discovered_domain && (
                        <a
                            href={`https://${card.discovered_domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`logo-review-card-${card.outcome_id}-open-domain`}
                            className="mt-1 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md border border-cyan-300 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 hover:border-cyan-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 transition-colors no-underline"
                        >
                            <Globe size={16} weight="bold" />
                            <span className="font-semibold">Open Domain</span>
                            <ArrowSquareOut size={13} />
                            <span className="ml-1 text-xs font-normal opacity-80">
                                — Verify the website is correct, then choose an action.
                            </span>
                        </a>
                    )}

                    {/* Skipped marker */}
                    {wasSkipped && (
                        <div className="text-[11px] text-slate-500 italic" data-testid={`logo-review-card-${card.outcome_id}-skipped-marker`}>
                            You skipped this on {new Date(card.skipped_at).toLocaleString()}.
                        </div>
                    )}

                    {/* Outcome summary banner for terminal states */}
                    {isApproved && (
                        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid={`logo-review-card-${card.outcome_id}-approved-banner`}>
                            <CheckCircle size={13} weight="fill" className="inline mr-1" />
                            Approved by <strong>{card.reviewed_by}</strong> at {new Date(card.reviewed_at).toLocaleString()} · Repository updated.
                        </div>
                    )}
                    {isHumanCorrected && (
                        <CorrectionTrailBanner card={card} />
                    )}
                    {isRejected && (
                        <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 space-y-1" data-testid={`logo-review-card-${card.outcome_id}-rejected-banner`}>
                            <div>
                                <XCircle size={13} weight="fill" className="inline mr-1" />
                                Rejected by <strong>{card.reviewed_by}</strong> at {new Date(card.reviewed_at).toLocaleString()}.
                            </div>
                            {card.rejection_reason && <div className="italic">&ldquo;{card.rejection_reason}&rdquo;</div>}
                            <button
                                type="button"
                                onClick={handleUndoReject}
                                disabled={busy}
                                data-testid={`logo-review-card-${card.outcome_id}-undo-reject`}
                                className="mt-1 text-rose-700 hover:text-rose-900 underline font-medium"
                            >
                                Undo (within 7 days)
                            </button>
                        </div>
                    )}

                    {/* Iter 87 — Secondary action row: Approve · Correct & Approve · Reject ONLY */}
                    {isPending && mode === "idle" && (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            <button
                                type="button"
                                onClick={handleApprove}
                                disabled={busy}
                                data-testid={`logo-review-card-${card.outcome_id}-approve`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                            >
                                <CheckCircle size={14} weight="fill" />
                                Approve
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("correct")}
                                disabled={busy}
                                data-testid={`logo-review-card-${card.outcome_id}-correct-approve`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-cyan-300 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 transition-colors disabled:opacity-50"
                            >
                                <PencilSimple size={14} weight="bold" />
                                Correct &amp; Approve
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("reject")}
                                disabled={busy}
                                data-testid={`logo-review-card-${card.outcome_id}-reject`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 transition-colors disabled:opacity-50"
                            >
                                <XCircle size={14} weight="bold" />
                                Reject
                            </button>
                        </div>
                    )}

                    {/* Reject reason form */}
                    {isPending && mode === "reject" && (
                        <div className="rounded border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid={`logo-review-card-${card.outcome_id}-reject-form`}>
                            <div className="text-xs font-semibold text-rose-900 flex items-center gap-1">
                                <Warning size={13} weight="fill" /> Reject — required reason (min 5 chars):
                            </div>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={2}
                                maxLength={500}
                                placeholder="e.g. Domain points to a parent holding company, not the listed entity."
                                className="w-full text-sm px-2 py-1.5 rounded border border-rose-200 focus:outline-none focus:ring-2 focus:ring-rose-200"
                                data-testid={`logo-review-card-${card.outcome_id}-reject-reason`}
                            />
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleReject}
                                    disabled={busy || rejectReason.trim().length < 5}
                                    className="px-3 py-1.5 rounded text-xs font-medium bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                                    data-testid={`logo-review-card-${card.outcome_id}-reject-confirm`}
                                >
                                    Confirm Reject
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setMode("idle"); setRejectReason(""); }}
                                    className="px-3 py-1.5 rounded text-xs font-medium text-slate-600 hover:bg-slate-100"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Iter 87 — Correct & Approve form (AI vs Human side-by-side) */}
                    {isPending && mode === "correct" && (
                        <div className="rounded border border-cyan-300 bg-cyan-50/40 p-3 space-y-3" data-testid={`logo-review-card-${card.outcome_id}-correct-form`}>
                            <div className="text-xs font-semibold text-cyan-900 flex items-center gap-1.5">
                                <Sparkle size={13} weight="fill" /> Correct &amp; Approve — make at least one change to the AI proposal.
                            </div>

                            {/* Domain row */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">AI Proposed Domain</label>
                                    <div
                                        className="w-full text-sm font-mono px-2 py-1.5 rounded border border-slate-200 bg-slate-100 text-slate-600 truncate"
                                        title={card.discovered_domain || "—"}
                                        data-testid={`logo-review-card-${card.outcome_id}-ai-domain`}
                                    >
                                        {card.discovered_domain || "—"}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wide font-semibold text-cyan-800 mb-1">Correct Domain</label>
                                    <input
                                        type="text"
                                        value={correctedDomain}
                                        onChange={(e) => setCorrectedDomain(e.target.value)}
                                        placeholder="example.com"
                                        className="w-full text-sm font-mono px-2 py-1.5 rounded border border-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-200"
                                        data-testid={`logo-review-card-${card.outcome_id}-corrected-domain`}
                                    />
                                </div>
                            </div>

                            {/* Logo URL row */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">AI Proposed Logo URL</label>
                                    <div
                                        className="w-full text-xs font-mono px-2 py-1.5 rounded border border-slate-200 bg-slate-100 text-slate-600 truncate"
                                        title={card.candidate_logo_url || "—"}
                                        data-testid={`logo-review-card-${card.outcome_id}-ai-logo-url`}
                                    >
                                        {card.candidate_logo_url || "—"}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wide font-semibold text-cyan-800 mb-1">Correct Logo URL (optional)</label>
                                    <input
                                        type="text"
                                        value={correctedLogoUrl}
                                        onChange={(e) => setCorrectedLogoUrl(e.target.value)}
                                        placeholder="https://example.com/logo.svg"
                                        disabled={!!correctedLogoFile}
                                        className="w-full text-xs font-mono px-2 py-1.5 rounded border border-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-200 disabled:bg-slate-100 disabled:text-slate-400"
                                        data-testid={`logo-review-card-${card.outcome_id}-corrected-logo-url`}
                                    />
                                </div>
                            </div>

                            {/* Upload logo */}
                            <div>
                                <label className="block text-[10px] uppercase tracking-wide font-semibold text-cyan-800 mb-1">
                                    Upload Logo (PNG / SVG / JPG / WEBP · max 2 MB)
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".png,.svg,.jpg,.jpeg,.webp,image/png,image/svg+xml,image/jpeg,image/webp"
                                        onChange={(e) => setCorrectedLogoFile(e.target.files?.[0] || null)}
                                        className="block text-xs file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-cyan-100 file:text-cyan-800 hover:file:bg-cyan-200"
                                        data-testid={`logo-review-card-${card.outcome_id}-corrected-logo-file`}
                                    />
                                    {correctedLogoFile && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setCorrectedLogoFile(null);
                                                if (fileInputRef.current) fileInputRef.current.value = "";
                                            }}
                                            className="text-[11px] text-rose-700 hover:underline"
                                            data-testid={`logo-review-card-${card.outcome_id}-corrected-logo-file-clear`}
                                        >
                                            Clear file
                                        </button>
                                    )}
                                </div>
                                {correctedLogoFile && (
                                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500">
                                        <UploadSimple size={11} />
                                        {correctedLogoFile.name} · {(correctedLogoFile.size / 1024).toFixed(1)} KB
                                    </div>
                                )}
                                <div className="mt-1 text-[10px] text-slate-500 italic">
                                    Tip: if you upload a file, the URL input above is ignored.
                                </div>
                            </div>

                            {/* Note */}
                            <div>
                                <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-600 mb-1">Note (optional, audit-only)</label>
                                <input
                                    type="text"
                                    value={correctionNote}
                                    onChange={(e) => setCorrectionNote(e.target.value)}
                                    maxLength={300}
                                    placeholder="e.g. AI returned the parent holding company; swapped for the listed subsidiary."
                                    className="w-full text-sm px-2 py-1.5 rounded border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-200"
                                    data-testid={`logo-review-card-${card.outcome_id}-correction-note`}
                                />
                            </div>

                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={handleCorrectApprove}
                                    disabled={busy}
                                    className="px-3 py-1.5 rounded text-xs font-medium bg-cyan-700 text-white hover:bg-cyan-800 disabled:opacity-50"
                                    data-testid={`logo-review-card-${card.outcome_id}-correct-confirm`}
                                >
                                    {busy ? "Saving…" : "Save & Approve"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMode("idle");
                                        setCorrectedLogoFile(null);
                                        setCorrectionNote("");
                                        if (fileInputRef.current) fileInputRef.current.value = "";
                                    }}
                                    className="px-3 py-1.5 rounded text-xs font-medium text-slate-600 hover:bg-slate-100"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}

/** Iter 87 — banner shown on cards in the `human_corrected` terminal state.
 *  Shows AI Proposal → Human Correction → Repository transformation
 *  so the audit trail is visible without leaving the review surface. */
function CorrectionTrailBanner({ card }) {
    const aiDomain = card.discovered_domain || "—";
    const aiLogo = card.candidate_logo_url || "—";
    const humanDomain = card.corrected_domain || card.edited_domain || "(unchanged)";
    const humanLogo = card.corrected_logo_url || card.edited_logo_url || "(unchanged)";
    return (
        <div
            className="rounded border border-cyan-300 bg-cyan-50 px-3 py-2 text-xs text-cyan-900 space-y-1"
            data-testid={`logo-review-card-${card.outcome_id}-human-corrected-banner`}
        >
            <div className="font-semibold flex items-center gap-1">
                <Sparkle size={13} weight="fill" />
                Human Corrected by <strong>{card.reviewed_by}</strong> at {new Date(card.reviewed_at).toLocaleString()} · Repository updated.
            </div>
            <table className="text-[11px] w-full">
                <tbody>
                    <tr>
                        <td className="w-24 text-slate-500 uppercase tracking-wide font-semibold">AI Domain</td>
                        <td className="font-mono text-slate-700 truncate" data-testid={`logo-review-card-${card.outcome_id}-trail-ai-domain`}>{aiDomain}</td>
                    </tr>
                    <tr>
                        <td className="text-cyan-700 uppercase tracking-wide font-semibold">Human Domain</td>
                        <td className="font-mono text-cyan-900 truncate" data-testid={`logo-review-card-${card.outcome_id}-trail-human-domain`}>{humanDomain}</td>
                    </tr>
                    <tr>
                        <td className="text-slate-500 uppercase tracking-wide font-semibold">AI Logo</td>
                        <td className="font-mono text-slate-700 truncate" title={aiLogo} data-testid={`logo-review-card-${card.outcome_id}-trail-ai-logo`}>{aiLogo}</td>
                    </tr>
                    <tr>
                        <td className="text-cyan-700 uppercase tracking-wide font-semibold">Human Logo</td>
                        <td className="font-mono text-cyan-900 truncate" title={humanLogo} data-testid={`logo-review-card-${card.outcome_id}-trail-human-logo`}>{humanLogo}</td>
                    </tr>
                    {card.correction_note && (
                        <tr>
                            <td className="text-slate-500 uppercase tracking-wide font-semibold">Note</td>
                            <td className="italic text-slate-700" data-testid={`logo-review-card-${card.outcome_id}-trail-note`}>&ldquo;{card.correction_note}&rdquo;</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
